import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { ByzMuiIconButton } from "../buttons/ByzMuiIconButton";
import ByzMuiButton from "../buttons/ByzMuiButton";
import { axiosInstance } from "../../../../api/config/axiosInstance";
import ByzMuiSnackBar from "../ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzOkeyButton from "../buttons/ByzOkeyButton";

interface ByzDeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  patientId?: string;
  url?: string;
  onSuccess?: (success: boolean) => void;
  title?: string;
  content?: string;
  onConfirm?: () => Promise<boolean | void> | boolean | void;
  customUrl?: string;
  onDelete?: () => Promise<boolean>;
  successMessage?: string;
  errorMessage?: string;
  hideSnackbar?: boolean;
}

const ByzDeleteConfirmDialog = ({
  open,
  onClose,
  patientId,
  url = "/auth/v1/MedCardService/Delete/V1",
  onSuccess,
  title,
  content,
  onConfirm,
  customUrl,
  onDelete,
  successMessage,
  errorMessage,
  hideSnackbar = false,
}: ByzDeleteConfirmDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastTitle, setToastTitle] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const { t } = useTranslate();

  const showToast = (type: "success" | "error", title: string, message: string) => {
    setToastType(type);
    setToastTitle(title);
    setToastMessage(message);
    setToastOpen(true);
  };

  const axiosDelete = async (): Promise<boolean> => {
    const apiUrl = customUrl || `${url}/${patientId}`;
    const response = await axiosInstance.delete(
      `${process.env.REACT_APP_API_URL}${apiUrl}`
    );
    return response.status === 200;
  };

  const handleDelete = async () => {
    setLoading(true);
    let success = false;

    try {
      if (onConfirm) {
        const r = await onConfirm();
        success = r !== false;
      } else if (onDelete) {
        success = await onDelete();
      } else {
        success = await axiosDelete();
      }

      if (!hideSnackbar) {
        if (success) {
          showToast("success", t("toast.title"), successMessage || t("toast.content"));
        } else {
          showToast("error", t("toast.error-title"), errorMessage || t("toast.error-content"));
        }
      }

    } catch (error) {
      if (!hideSnackbar) {
        showToast("error", t("toast.error-title"), errorMessage || t("toast.error"));
      }
      success = false;
    } finally {
      onClose();
      onSuccess?.(success);
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth sx={{ borderRadius: 3 }}>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "0.9rem", sm: "1rem" },
            display: "flex",
            justifyContent: "space-between",
            pb: 1,
            px: 2,
          }}
        >
          {title || t("delete-confirm.title")}
          <ByzMuiIconButton onClick={onClose} sx={{ padding: 0 }}>
            <CloseIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
          </ByzMuiIconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            {content || t("delete-confirm.content")}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <ByzMuiButton
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={loading}
          >
            {t("buttons.cancel")}
          </ByzMuiButton>

          <ByzOkeyButton onClick={handleDelete} disabled={loading} loading={loading} />
        </DialogActions>
      </Dialog>

      <ByzMuiSnackBar
        open={toastOpen}
        type={toastType}
        title={toastTitle}
        message={toastMessage}
        onClose={() => setToastOpen(false)}
      />
    </>
  );
};

export default ByzDeleteConfirmDialog;
